/**
 * FAQ Data
 * Static data for frequently asked questions
 */

import { FAQItem, FAQCategory } from '../types/faq';

export const faqs: FAQItem[] = [
  // General Questions
  {
    id: 'faq-gen-001',
    category: 'general',
    question: 'How long is CPR certification valid?',
    answer:
      'Most CPR certifications are valid for 2 years. BLS (Basic Life Support) certification for healthcare providers is also valid for 2 years. After this period, you will need to renew your certification to maintain your credential.',
    order: 1,
  },
  {
    id: 'faq-gen-002',
    category: 'general',
    question: "What's the difference between CPR and BLS?",
    answer:
      'CPR (Cardiopulmonary Resuscitation) is designed for the general public and covers basic life-saving techniques. BLS (Basic Life Support) is specifically designed for healthcare professionals and includes more advanced techniques, team dynamics, and scenarios that healthcare workers encounter. BLS also covers bag-valve-mask ventilation and opioid overdose response.',
    order: 2,
  },
  {
    id: 'faq-gen-003',
    category: 'general',
    question: 'Do I need any prerequisites to take a CPR class?',
    answer:
      'No prerequisites are required for most CPR and AED certification courses. These classes are designed for beginners. For CPR renewal classes, you should have a previous certification (can be expired within 1 year). BLS for Healthcare Providers is recommended for those in or entering the medical field, but no prior medical training is required.',
    order: 3,
  },
  {
    id: 'faq-gen-004',
    category: 'general',
    question: 'What certification bodies do you use?',
    answer:
      'We offer certifications through the American Heart Association (AHA), American Safety & Health Institute (ASHI), and Red Cross. All three are nationally recognized and accepted by employers, schools, and licensing boards. We can help you determine which certification best fits your needs.',
    order: 4,
  },
  {
    id: 'faq-gen-005',
    category: 'general',
    question: 'How long does a typical CPR class take?',
    answer:
      'Class duration varies by course type. CPR & AED certification classes typically take 3-4 hours. Renewal classes are shorter at 2-3 hours. BLS for Healthcare Providers takes 4-5 hours due to the additional content and scenarios. Group training duration depends on the number of participants and specific course requirements.',
    order: 5,
  },

  // Booking & Payment
  {
    id: 'faq-book-001',
    category: 'booking-payment',
    question: 'How do I book a class?',
    answer:
      'You can book a class by visiting our Book Now page and filling out the booking request form. Let us know your preferred date, location, and course type. We will confirm availability within 24 hours and send you enrollment details. You can also call our office directly to book over the phone.',
    order: 1,
  },
  {
    id: 'faq-book-002',
    category: 'booking-payment',
    question: 'Can I pay at the class?',
    answer:
      'Yes! We accept payment at the class via cash, credit card, or check. However, we offer early-bird discounts if you register and pay 7+ days in advance. Online payment options are also available for your convenience.',
    order: 2,
  },
  {
    id: 'faq-book-003',
    category: 'booking-payment',
    question: "What's your cancellation policy?",
    answer:
      'If you need to cancel or reschedule, please notify us at least 48 hours before your scheduled class. Cancellations made with 48+ hours notice will receive a full refund or can be rescheduled at no charge. Cancellations made less than 48 hours before class may incur a rescheduling fee.',
    order: 3,
  },
  {
    id: 'faq-book-004',
    category: 'booking-payment',
    question: 'Do you offer group discounts?',
    answer:
      'Yes! Groups of 5 or more receive discounted rates. For example, our CPR & AED certification is $70 per person for groups (regular price $85). For groups of 10 or more, we can also arrange on-site training at your location for even greater convenience and savings.',
    order: 4,
  },
  {
    id: 'faq-book-005',
    category: 'booking-payment',
    question: 'Can I get a receipt for my employer?',
    answer:
      'Absolutely! We provide itemized receipts for all payments. Your certification card also serves as official documentation for your employer. If you need additional documentation or letters of completion, just let us know and we can provide those as well.',
    order: 5,
  },

  // Certification & Renewals
  {
    id: 'faq-cert-001',
    category: 'certification-renewals',
    question: 'Will I receive my certification card the same day?',
    answer:
      'Yes! Upon successful completion of the course, you will receive a digital certification card via email the same day. Your physical certification card will be mailed to you within 5 business days. The digital card is valid and accepted by all employers and institutions.',
    order: 1,
  },
  {
    id: 'faq-cert-002',
    category: 'certification-renewals',
    question: 'How do I renew my CPR certification?',
    answer:
      'If you have a current or recently expired certification (within 1 year), you can take our CPR Renewal class, which is shorter and less expensive than the full certification course. Simply book a renewal class, and you will review critical skills and receive a new 2-year certification.',
    order: 2,
  },
  {
    id: 'faq-cert-003',
    category: 'certification-renewals',
    question: 'Is online CPR certification accepted?',
    answer:
      'It depends on your employer or licensing requirements. Some accept fully online certifications, while others require hands-on skills assessment. We offer blended learning options that combine online theory with in-person skills practice, which is widely accepted. Contact us to discuss which format best meets your needs.',
    order: 3,
  },
  {
    id: 'faq-cert-004',
    category: 'certification-renewals',
    question: 'What if my certification has been expired for more than a year?',
    answer:
      'If your certification expired more than a year ago, you will need to take the full certification course rather than a renewal class. This ensures you receive comprehensive training on any updated techniques and guidelines. The full course provides a complete refresher and new certification.',
    order: 4,
  },
  {
    id: 'faq-cert-005',
    category: 'certification-renewals',
    question: 'Can I verify my certification online?',
    answer:
      'Yes! Certification verification depends on the certifying body. American Heart Association certifications can be verified online through their database. We also provide verification letters upon request if your employer needs additional documentation.',
    order: 5,
  },

  // Group Training
  {
    id: 'faq-group-001',
    category: 'group-training',
    question: 'Do you offer on-site training at our facility?',
    answer:
      'Yes! We bring all equipment, mannequins, and materials directly to your workplace, school, or facility. On-site training is available for groups of 10 or more and can be scheduled during business hours, evenings, or weekends to accommodate your team.',
    order: 1,
  },
  {
    id: 'faq-group-002',
    category: 'group-training',
    question: "What's the minimum group size for on-site training?",
    answer:
      'The minimum group size for on-site training is 10 participants. For groups smaller than 10, we recommend attending one of our regularly scheduled classes at our training centers in Orlando or Atlanta. Group discounts (5+ people) are available for scheduled classes.',
    order: 2,
  },
  {
    id: 'faq-group-003',
    category: 'group-training',
    question: 'Can you come to our facility outside of Orlando and Atlanta?',
    answer:
      'Yes! We offer mobile on-site training within 50 miles of our Orlando and Atlanta locations. For locations beyond that range, please contact us to discuss arrangements. Additional travel fees may apply for distant locations.',
    order: 3,
  },
  {
    id: 'faq-group-004',
    category: 'group-training',
    question: 'How long does group training take?',
    answer:
      'Group training duration depends on the number of participants and course type. For standard CPR/AED training, plan for approximately 4-5 hours for groups of 10-20 people. Larger groups may require additional time or multiple sessions. We can customize the schedule to fit your operational needs.',
    order: 4,
  },
  {
    id: 'faq-group-005',
    category: 'group-training',
    question: 'What types of organizations do you train?',
    answer:
      'We train a wide variety of organizations including corporate offices, healthcare facilities, schools, daycare centers, fitness centers, construction sites, restaurants, hotels, churches, and community organizations. We can customize training to address your industry-specific emergency scenarios.',
    order: 5,
  },
];

/**
 * Get FAQs by category
 */
export const getFAQsByCategory = (category: FAQCategory): FAQItem[] => {
  return faqs
    .filter((faq) => faq.category === category)
    .sort((a, b) => a.order - b.order);
};

/**
 * Get all FAQs grouped by category
 */
export const getFAQsGrouped = () => {
  const categories: FAQCategory[] = [
    'general',
    'booking-payment',
    'certification-renewals',
    'group-training',
  ];

  return categories.map((category) => ({
    category,
    categoryTitle: getCategoryTitle(category),
    faqs: getFAQsByCategory(category),
  }));
};

/**
 * Get category display title
 */
export const getCategoryTitle = (category: FAQCategory): string => {
  const titles: Record<FAQCategory, string> = {
    general: 'General Questions',
    'booking-payment': 'Booking & Payment',
    'certification-renewals': 'Certification & Renewals',
    'group-training': 'Group Training',
  };
  return titles[category];
};

/**
 * Search FAQs by keyword
 */
export const searchFAQs = (keyword: string): FAQItem[] => {
  const lowerKeyword = keyword.toLowerCase();
  return faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(lowerKeyword) ||
      faq.answer.toLowerCase().includes(lowerKeyword)
  );
};
